import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ThemeContext } from "../hooks/ThemeContext";
import { api } from "../services/baseUrl";
import {
  CheckCircle2,
  ArrowRight,
  Zap,
  CreditCard,
  Calendar,
} from "lucide-react";

/* Plans de base (prix unique) */
const PLANS = [
  {
    name: "Vitrine",
    price: 490,
    unit: "projet",
    desc: "Idéal pour présenter votre activité avec un site sobre et efficace.",
    items: [
      "Design sur-mesure (pas de template)",
      "Jusqu'à 5 pages",
      "Responsive mobile",
      "SEO technique de base",
      "Formulaire de contact",
      "Livraison en 7-10 jours",
    ],
    cta: "Commander",
    accent: "#f43f5e",
    featured: false,
    installments: 3,
  },
  {
    name: "Pro",
    price: 990,
    unit: "projet",
    desc: "Pour les projets ambitieux qui demandent plus de pages et de fonctionnalités.",
    items: [
      "Tout ce qui est dans Vitrine",
      "Jusqu'à 12 pages",
      "Blog ou espace actus",
      "SEO avancé + sitemap",
      "Animations & micro-interactions",
      "1 mois de support inclus",
    ],
    cta: "Commander",
    accent: "#a855f7",
    featured: true,
    installments: 4,
  },
  {
    name: "E-commerce",
    price: 1490,
    unit: "projet",
    desc: "Boutique en ligne complète avec paiement Stripe et gestion catalogue.",
    items: [
      "Tout ce qui est dans Pro",
      "Boutique Stripe",
      "Espace client & commandes",
      "Gestion catalogue produits",
      "Paiement sécurisé",
      "2 mois de support inclus",
    ],
    cta: "Commander",
    accent: "#6366f1",
    featured: false,
    installments: 6,
  },
];

const FAQ = [
  {
    q: "Puis-je avoir un devis personnalisé ?",
    a: "Oui, chaque projet est unique. Contactez-nous pour un devis gratuit et sans engagement adapté à votre besoin.",
  },
  {
    q: "Comment se déroule le paiement ?",
    a: "Deux options: en une fois (50% à la commande, 50% à la livraison), ou une option sans engagement a 3000€. Les 3 formules mensuelles restent avec engagement sur 36 mois.",
  },
  {
    q: "Combien de temps prend la réalisation ?",
    a: "Entre 7 et 21 jours selon la complexité. Un planning est fourni dès le début du projet.",
  },
  {
    q: "Que se passe-t-il après la livraison ?",
    a: "Vous recevez le code source complet. Le support inclus couvre les corrections post-livraison.",
  },
];

const fmt = (n) =>
  new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 0 }).format(n);

const MONTHLY_COMMITMENT_MONTHS = 36;

const Pricing = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const [openFaq, setOpenFaq] = useState(null);
  const [payMode, setPayMode] = useState("once"); // "once" | "installments"
  const [dynamicPrices, setDynamicPrices] = useState({});
  const [noCommitmentAmount, setNoCommitmentAmount] = useState(3000);
  const [hostingPriceLow, setHostingPriceLow] = useState(12);
  const [hostingPriceHigh, setHostingPriceHigh] = useState(15);
  const g = "linear-gradient(135deg, #f43f5e 0%, #a855f7 50%, #6366f1 100%)";
  const muted = isDark ? "text-gray-500" : "text-gray-400";

  useEffect(() => {
    api
      .get("/site-settings")
      .then((res) => {
        const plans = res.data?.data?.pricing_plans;
        const noCommitAmount = Number(
          res.data?.data?.no_commitment_amount ?? 3000,
        );

        if (Number.isFinite(noCommitAmount) && noCommitAmount >= 0) {
          setNoCommitmentAmount(noCommitAmount);
        }

        const priceLow = Number(res.data?.data?.hosting_price_low ?? 12);
        const priceHigh = Number(res.data?.data?.hosting_price_high ?? 15);
        if (Number.isFinite(priceLow)) setHostingPriceLow(priceLow);
        if (Number.isFinite(priceHigh)) setHostingPriceHigh(priceHigh);

        if (Array.isArray(plans) && plans.length > 0) {
          const priceMap = {};
          plans.forEach((p) => {
            if (p.name) {
              priceMap[p.name] = {
                price: Number(p.price ?? 0),
                installments: Number(p.installments ?? 1),
              };
            }
          });
          setDynamicPrices(priceMap);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div
      className="flex-1 flex flex-col"
      style={{ backgroundColor: isDark ? "#07070a" : "#ffffff" }}
    >
      {/* Hero */}
      <section
        className={`border-b px-6 py-16 text-center ${isDark ? "border-white/[0.06] text-white" : "border-gray-100 text-gray-900"}`}
      >
        <div className="max-w-2xl mx-auto flex flex-col gap-5">
          <h1
            className="text-4xl lg:text-5xl font-black"
            style={{ letterSpacing: "-0.03em" }}
          >
            Simple,{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: g }}
            >
              transparent
            </span>
          </h1>
          <p className={`text-base max-w-md mx-auto leading-relaxed ${muted}`}>
            Pas de surprises. Des forfaits clairs pour chaque besoin, avec la
            possibilité de payer en plusieurs fois.
          </p>

          {/* Toggle paiement */}
          <div
            className={`inline-flex items-center rounded-xl border p-1 mx-auto mt-2 ${
              isDark
                ? "border-white/[0.08] bg-white/[0.03]"
                : "border-gray-200 bg-gray-50"
            }`}
          >
            <button
              onClick={() => setPayMode("once")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                payMode === "once"
                  ? "text-white shadow-md"
                  : isDark
                    ? "text-gray-400 hover:text-gray-200"
                    : "text-gray-500 hover:text-gray-700"
              }`}
              style={payMode === "once" ? { backgroundImage: g } : {}}
            >
              <CreditCard className="w-3.5 h-3.5" />
              En une fois
            </button>
            <button
              onClick={() => setPayMode("installments")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                payMode === "installments"
                  ? "text-white shadow-md"
                  : isDark
                    ? "text-gray-400 hover:text-gray-200"
                    : "text-gray-500 hover:text-gray-700"
              }`}
              style={payMode === "installments" ? { backgroundImage: g } : {}}
            >
              <Calendar className="w-3.5 h-3.5" />
              En plusieurs fois
            </button>
          </div>

          {payMode === "installments" && (
            <p className={`text-xs ${muted}`}>
              Paiement échelonné sans frais — le montant total reste identique.
            </p>
          )}
        </div>
      </section>

      {/* Plans */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {PLANS.map((plan) => {
            const dynPlan = dynamicPrices[plan.name];
            const price = dynPlan?.price ?? plan.price;
            const monthly =
              payMode === "installments"
                ? Math.ceil(price / MONTHLY_COMMITMENT_MONTHS)
                : null;

            return (
              <div
                key={plan.name}
                className={`relative rounded-2xl border flex flex-col gap-6 pt-7 px-7 pb-0 overflow-hidden transition-all ${
                  plan.featured
                    ? isDark
                      ? "border-white/[0.15] bg-white/[0.05]"
                      : "border-gray-300 bg-gray-50 shadow-xl"
                    : isDark
                      ? "border-white/[0.08] bg-white/[0.03]"
                      : "border-gray-200 bg-white"
                }`}
              >
                {plan.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span
                      className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black text-white uppercase tracking-wider"
                      style={{ background: g }}
                    >
                      <Zap className="w-3 h-3" /> Le plus choisi
                    </span>
                  </div>
                )}

                {/* Header */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ background: plan.accent }}
                    />
                    <span
                      className={`text-xs font-bold uppercase tracking-widest ${isDark ? "text-gray-500" : "text-gray-400"}`}
                    >
                      {plan.name}
                    </span>
                  </div>

                  {payMode === "once" ? (
                    <div className="flex items-baseline gap-1 mb-2">
                      <span
                        className={`text-4xl font-black ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        {fmt(price)}€
                      </span>
                      <span className={`text-sm ${muted}`}>/{plan.unit}</span>
                    </div>
                  ) : (
                    <div className="mb-2">
                      <div className="flex items-baseline gap-1">
                        <span
                          className={`text-4xl font-black ${isDark ? "text-white" : "text-gray-900"}`}
                        >
                          {fmt(monthly)}€
                        </span>
                        <span className={`text-sm ${muted}`}>/mois</span>
                      </div>
                      <p className={`text-xs mt-1 ${muted}`}>
                        Soit {fmt(price)}€ sur {MONTHLY_COMMITMENT_MONTHS} mois
                      </p>
                    </div>
                  )}

                  <p className={`text-sm leading-relaxed ${muted}`}>
                    {plan.desc}
                  </p>
                  {payMode === "once" && (
                    <p
                      className={`text-xs mt-2 flex items-center gap-1 ${muted}`}
                    >
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-400 flex-shrink-0" />
                      Hébergement mensuel en option&nbsp;: dès&nbsp;
                      {hostingPriceLow}€/mois
                    </p>
                  )}
                </div>

                {/* Features */}
                <ul className="flex flex-col gap-2.5">
                  {plan.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm">
                      <CheckCircle2
                        className="w-4 h-4 flex-shrink-0 mt-0.5"
                        style={{ color: plan.accent }}
                      />
                      <span
                        className={isDark ? "text-gray-400" : "text-gray-600"}
                      >
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Link
                  to={`/contact?plan=${encodeURIComponent(plan.name)}&commande=1&mode=${encodeURIComponent(payMode)}&amount=${encodeURIComponent(String(price))}`}
                  className="inline-flex items-center justify-center gap-2 py-4 text-sm font-bold text-white transition-all mt-auto w-full rounded-b-2xl"
                  style={{
                    background: plan.featured
                      ? g
                      : `linear-gradient(135deg, ${plan.accent}cc, ${plan.accent})`,
                  }}
                >
                  {plan.cta} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            );
          })}
        </div>

        {/* Add-on hébergement — mode paiement unique */}
        {payMode === "once" && (
          <div
            className={`mt-6 rounded-2xl border p-6 flex flex-wrap items-center justify-between gap-4 ${
              isDark
                ? "border-sky-500/20 bg-sky-500/[0.04]"
                : "border-sky-200 bg-sky-50"
            }`}
          >
            <div className="flex items-start gap-3">
              <div
                className="h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{
                  background: "linear-gradient(135deg,#0ea5e930,#6366f130)",
                }}
              >
                <span className="text-sky-400 text-lg">🌐</span>
              </div>
              <div>
                <p
                  className={`text-sm font-bold mb-0.5 ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  Complétez avec l'hébergement mensuel
                </p>
                <p className={`text-xs leading-relaxed max-w-md ${muted}`}>
                  Votre site en une fois + hébergement récurrent&nbsp;:{" "}
                  <span className={isDark ? "text-sky-400" : "text-sky-600"}>
                    {hostingPriceLow}€/mois
                  </span>
                  {" ou "}
                  <span className={isDark ? "text-sky-400" : "text-sky-600"}>
                    {hostingPriceHigh}€/mois
                  </span>{" "}
                  selon le type de site. Domaine, SSL et sauvegardes inclus.
                </p>
              </div>
            </div>
            <Link
              to="/contact"
              className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-2xl text-sm font-bold text-white shadow-lg shadow-sky-500/30 ring-1 ring-white/10 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sky-500/50 hover:brightness-110 active:scale-95"
              style={{
                background: "linear-gradient(135deg, #0ea5e9, #6366f1)",
              }}
            >
              En savoir plus <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {/* Bloc paiement plusieurs fois */}
        {payMode === "installments" && (
          <div
            className={`mt-6 rounded-2xl border p-6 ${
              isDark
                ? "border-white/[0.08] bg-white/[0.03]"
                : "border-gray-200 bg-gray-50"
            }`}
          >
            <div className="flex items-center gap-2.5 mb-4">
              <div
                className="h-8 w-8 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: "linear-gradient(135deg,#a855f730,#6366f130)",
                }}
              >
                <Calendar className="w-4 h-4 text-violet-400" />
              </div>
              <p
                className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Comment fonctionne le paiement en plusieurs fois&nbsp;?
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  n: "1",
                  title: "Sans acompte",
                  desc: "Aucun acompte de 50% à la commande en mode paiement mensuel.",
                },
                {
                  n: "2",
                  title: "Versements mensuels",
                  desc: "Paiement mensuel régulier, sans frais supplémentaires.",
                },
                {
                  n: "3",
                  title: "Engagement 3 ans",
                  desc: "Le plan mensuel fonctionne sur un engagement de 36 mois.",
                },
              ].map(({ n, title, desc }) => (
                <div key={n} className="flex gap-3">
                  <span
                    className="h-6 w-6 rounded-full flex-shrink-0 flex items-center justify-center text-[11px] font-black text-white mt-0.5"
                    style={{ background: g }}
                  >
                    {n}
                  </span>
                  <div>
                    <p
                      className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                    >
                      {title}
                    </p>
                    <p className={`text-xs mt-0.5 leading-relaxed ${muted}`}>
                      {desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {payMode === "installments" && (
          <div
            className={`mt-4 rounded-2xl border p-5 ${
              isDark
                ? "border-white/[0.08] bg-white/[0.02]"
                : "border-gray-200 bg-white"
            }`}
          >
            <p
              className={`text-xs font-bold uppercase tracking-widest mb-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}
            >
              Sans engagement
            </p>
            <p
              className={`text-xl font-black mb-2 ${isDark ? "text-white" : "text-gray-900"}`}
            >
              {fmt(noCommitmentAmount)}€
            </p>
            <p className={`text-sm ${muted}`}>
              Option sans engagement: montant fixe a {fmt(noCommitmentAmount)}€.
              Les 3 autres formules en paiement mensuel restent avec engagement
              sur 36 mois.
            </p>
            <Link
              to={`/contact?plan=${encodeURIComponent("Site sans engagement")}&commande=1&mode=no-commitment&amount=${encodeURIComponent(String(noCommitmentAmount))}`}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:-translate-y-0.5"
              style={{ background: g }}
            >
              Commander sans engagement <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {/* Hébergement */}
        <div
          className={`mt-6 rounded-2xl border p-7 ${
            isDark
              ? "border-white/[0.08] bg-white/[0.03]"
              : "border-gray-200 bg-white"
          }`}
        >
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-sky-400" />
                <span
                  className={`text-xs font-bold uppercase tracking-widest ${isDark ? "text-gray-500" : "text-gray-400"}`}
                >
                  Hébergement
                </span>
              </div>
              <div className="flex items-baseline gap-3 flex-wrap mb-1">
                <span
                  className={`text-2xl font-black ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  {hostingPriceLow}€
                </span>
                <span className={`text-base font-semibold -ml-2 ${muted}`}>
                  /mois
                </span>
                <span className={`text-lg ${muted}`}>ou</span>
                <span
                  className={`text-2xl font-black ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  {hostingPriceHigh}€
                </span>
                <span className={`text-base font-semibold -ml-2 ${muted}`}>
                  /mois
                </span>
              </div>
              <p className={`text-sm mt-2 max-w-lg leading-relaxed ${muted}`}>
                Hébergement professionnel de votre site. Le tarif dépend du type
                et de la complexité du site — un devis précis est fourni à la
                livraison.
              </p>
              <ul className="mt-4 flex flex-col gap-2">
                {[
                  "Nom de domaine inclus la 1ère année",
                  "Certificat SSL (HTTPS)",
                  "Sauvegardes automatiques",
                  "Support technique inclus",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-sky-400 flex-shrink-0" />
                    <span
                      className={isDark ? "text-gray-400" : "text-gray-600"}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <Link
              to="/contact"
              className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-2xl text-sm font-bold text-white shadow-lg shadow-sky-500/30 ring-1 ring-white/10 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sky-500/50 hover:brightness-110 active:scale-95"
              style={{
                background: "linear-gradient(135deg, #0ea5e9, #6366f1)",
              }}
            >
              En savoir plus <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Sur-mesure */}
        <div
          className={`mt-6 rounded-2xl border p-7 flex flex-wrap items-start justify-between gap-5 ${
            isDark
              ? "border-white/[0.08] bg-white/[0.03]"
              : "border-gray-200 bg-white"
          }`}
        >
          <div>
            <p
              className={`text-xs font-bold uppercase tracking-widest mb-1 ${isDark ? "text-gray-600" : "text-gray-400"}`}
            >
              Sur-mesure
            </p>
            <p
              className={`font-bold text-lg ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Besoin d'un projet spécifique ?
            </p>
            <p className={`text-sm mt-1 ${muted}`}>
              Portail client, API, automatisation, architecture complexe. Devis
              gratuit.
            </p>
          </div>
          <Link
            to="/contact"
            className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-2xl text-sm font-bold text-white shadow-lg shadow-fuchsia-500/30 ring-1 ring-white/10 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-fuchsia-500/50 hover:brightness-110 active:scale-95"
            style={{ background: g }}
          >
            Discutons-en <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section
        className={`border-t px-6 py-16 ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
      >
        <div className="max-w-2xl mx-auto">
          <h2
            className={`text-2xl font-black mb-8 ${isDark ? "text-white" : "text-gray-900"}`}
            style={{ letterSpacing: "-0.02em" }}
          >
            Questions fréquentes
          </h2>
          <div className="flex flex-col gap-2">
            {FAQ.map((item, i) => (
              <div
                key={i}
                className={`rounded-xl border overflow-hidden ${isDark ? "border-white/[0.07]" : "border-gray-100"}`}
              >
                <button
                  className={`w-full flex items-center justify-between px-5 py-4 text-left text-sm font-semibold transition-colors ${
                    isDark
                      ? "text-white hover:bg-white/[0.03]"
                      : "text-gray-900 hover:bg-gray-50"
                  }`}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  {item.q}
                  <span
                    className={`transition-transform ${openFaq === i ? "rotate-180" : ""} ${muted}`}
                  >
                    ▾
                  </span>
                </button>
                {openFaq === i && (
                  <div className={`px-5 pb-4 text-sm ${muted}`}>{item.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
